import React, {useEffect, useState} from "react";
import {Select, Stack, Theme, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import WindowContainer from "../../../../components/layout/WindowContainer";
import FilterSet from "../../../../components/selectors/Filterset";
import {targets, useReferentialContext} from "../../../../Context";
import {Player, Team} from "../../../../farsight-api";
import dayjs from "dayjs";
import TableDisplayGrid, {rowStyle} from "../../../../components/datagrids/shared/TableDisplayGrid";
import {DraftsBlock} from "../8_DraftScouting/TeamDrafts";
import TeamHistory from "../../../../components/datagrids/Competitive/TeamHistory";
import JunglePathing, {PathingMaps} from "../7_MacroScouting/JunglePathing";
import Proximities from "../../../../components/Charts/Proximities";
import SpecificGameSelector from "../../../../components/selectors/Competitive/SpecificGameSelector";
import {WardMapDraws} from "../7_MacroScouting/WardMaps";
import TimestampRangeSelect from "../../../../components/selectors/TimestampRangeSelect";
import EconomyScouting from "../EconomyScouting";
import StatSharePie from "../../../../components/Charts/StatShare";
import MenuItem from "@mui/material/MenuItem";
import Level1, {Level1Maps} from "../7_MacroScouting/Level1";

export default function ScoutingSummary() {
    const context = useReferentialContext();
    const [paramObject, setParamObject] = useState({ordering: '-games'} as any);
    const [team, setTeam] = useState(null as Team|null);
    const [players, setPlayers] = useState([] as Player[]);
    const role_ordered = {
        'TOP_LANE': 1,
        'JUNGLE': 2,
        'MID_LANE': 3,
        'BOT_LANE': 4,
        'UTILITY': 5,
    }
    const [wardMapSelection, setWardMapSelection] = useState([] as string[]);
    const [ingameIntervalWardMaps, setIngameInterval] = useState([0,600]);
    const [competitive_from, setCompetitiveFrom] = useState(dayjs().subtract(3, 'weeks'));
    const [competitive_until, setCompetitiveUntil] = useState(dayjs());

    const updateSelection = (gameSelected: any[]) => {
        setWardMapSelection(gameSelected.map(g => g.game_id));
    };

    useEffect(() => {
        if(team) context.farsightApi.player.list({current_team: team?.team_name, additional_filters:{role__isnull: false, is_sub__isnull: true}}).then(r => {

            // @ts-ignore
            setPlayers(r.sort((a, b) => role_ordered[a.role] - role_ordered[b.role]))
        });
    }, [team])



    return (
        <WindowContainer direction={"column"} spacing={3}>
            <FilterSet paramObject={paramObject}
                       setParamObject={setParamObject}
                       target={targets.competitive}
                       team={team} setTeam={setTeam}/>
            <SectionHeader text={'Champions'} vertical={false}/>
            {
                players.map(p => <PlayerChampionsBlock player={p}/>)
            }
            <SectionHeader text={'Recent Drafts'} vertical={false}/>
            {team ? <DraftsBlock paramObject={{
                team1: team.team_name,
                additional_filters: {date__gte: dayjs().subtract(3, 'weeks').toISOString()},
                watermark: team.team_name,
            }} target={targets.competitive}/> : <></>}
            <SectionHeader text={'Recent Games'} vertical={false}/>
            {
                team ?
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: "center",
                            width: "100%",
                            minHeight: '800px'
                        }}
                    >
                        <TeamHistory from={dayjs().subtract(3, 'weeks').unix()} until={dayjs().unix()} team={team ? team.team_name : ''} target={targets.competitive} />
                    </Stack>
                    : <></>
            }
            <SectionHeader text={'Recent Pathings'} vertical={false}/>
            {
                team ? <PathingMaps team={team} from={dayjs().subtract(3, 'weeks')} target={targets.competitive}/> : <></>
            }
            <SectionHeader text={'Level1s'} vertical={false}/>
            {
                team ? <Level1Maps team={team} from={dayjs().subtract(3, 'weeks')} target={targets.competitive}/> : <></>
            }
            <SectionHeader text={'Proximities Pre 15'} vertical={false}/>
            <Stack direction={'row'} sx={{width:'100%', minHeight:'400px'}} spacing={2}>
                {
                    players.map(p =>
                        <Stack direction={'column'} sx={{height:'100%', width:'20%'}} spacing={2}>
                            <SectionHeader text={p.player} vertical={false}/>
                            <Proximities target={targets.competitive}
                                         paramObject={{player: p.player, additional_filters : {date__gte:dayjs().subtract(3, 'weeks').toISOString()}, watermark: p.player}} timer={'pre15'}
                            />
                        </Stack>
                        )
                }
            </Stack>
            <SectionHeader text={'Proximities Post 15'} vertical={false}/>
            <Stack direction={'row'} sx={{width:'100%', minHeight:'400px'}} spacing={2}>
                {
                    players.map(p =>
                        <Stack direction={'column'} sx={{height:'100%', width:'20%'}} spacing={2}>
                            <SectionHeader text={p.player} vertical={false}/>
                            <Proximities target={targets.competitive}
                                         paramObject={{player: p.player, additional_filters : {date__gte:dayjs().subtract(3, 'weeks').toISOString()}, watermark: p.player}} timer={'post15'}
                            />
                        </Stack>
                    )
                }
            </Stack>
            <SectionHeader text={'Stats Evolutions'} vertical={false}/>
            <EconomyScouting/>
            <SectionHeader text={'Stat shares'} vertical={false}/>
            <Stack direction={'row'} sx={{width:'100%', minHeight:'400px'}} spacing={2}>
            {team ?
                ['damages__sum','total_golds__sum','total_cs__sum','deaths__sum','time_spent_dead__sum'].map(stat => <StatSharePie target={targets.competitive}
                                   paramObject={{
                                       team1: team.team_name,
                                       additional_filters: {date__gte: dayjs().subtract(3, 'weeks').toISOString()}
                                   }}
                                   stat={stat}/>)
                : <></>}
            </Stack>

        </WindowContainer>
    );
}


function PlayerChampionsBlock(props: {player: Player}){
    const [paramObject, setParamObject] = useState({ordering: '-games'} as any);
    const [competitive_from, set_competitive_from] = useState(dayjs().subtract(3, 'month'));
    const [soloq_from, set_soloq_from] = useState(dayjs().subtract(14, 'days'));
    const competitive_api = useReferentialContext().farsightApi.getSummariesApi(targets.competitive);
    const soloq_api = useReferentialContext().farsightApi.getSummariesApi(targets.soloq);
    const context = useReferentialContext();
    const default_metrics = ['games','performance','relative_performance','winrate'];
    const [meta_champions, set_meta_champions] = useState([] as any[]);

    const [request_parameters_1, set_request_parameters_1] = useState({
        player: props.player.player,
        groupby: 'champion',
        metrics: default_metrics.toString(),
        ordering: "-games",
        format: "json",
        additional_filters: {date__gte: competitive_from.toISOString()},
        watermark: `${competitive_from.toISOString()}${props.player.player}`
    } as any);
    const [request_parameters_2, set_request_parameters_2] = useState({
        competitive_player: props.player.player,
        groupby: 'champion',
        role: props.player.role,
        metrics: default_metrics.toString(),
        ordering: "-games",
        format: "json",
        additional_filters: {date__gte: soloq_from.toISOString()},
        watermark: `${soloq_from.toISOString()}${props.player.player}`
    }  as any);
    const [metrics, setMetrics] = useState(context.metricsReferential.filter(m => default_metrics.includes(m.code)));

    useEffect(() => {
        set_request_parameters_1({
            player: props.player.player,
            groupby: 'champion',
            metrics: default_metrics.toString(),
            ordering: "-games",
            format: "json",
            additional_filters: {date__gte: competitive_from.toISOString()},
            watermark: `${competitive_from.toISOString()}${props.player.player}`
        });
    }, [props.player, competitive_from]);

    useEffect(() => {
        console.log('Upated params')
        set_request_parameters_2({
            competitive_player: props.player.player,
            groupby: 'champion',
            role: props.player.role,
            metrics: default_metrics.toString(),
            ordering: "-games",
            format: "json",
            additional_filters: {date__gte: soloq_from.toISOString()},
            watermark: `${soloq_from.toISOString()}${props.player.player}`
        });
    }, [props.player, soloq_from])

    useEffect(() => {
        competitive_api.aggregate(
            {
                additional_filters: {
                    date__gte: competitive_from.toISOString(),
                    league__in: ['LEC,LCK,LPL,LCS,LFL,PCS']
                },
                role: props.player.role,
                ordering: "-games",
                format: "json",
                groupby: 'champion',
                metrics: default_metrics.toString(),
            } as any
        ).then(r => set_meta_champions(r))
    }, [props.player.role])

    return <Stack sx={{width:'100%', height:'850px'}} direction={'row'} spacing={3}>
                <SectionHeader text={props.player.player} vertical={true}/>
                <Stack  sx={{width:'33%', height:'850px'}} direction={'column'} spacing={3}>
                    <SectionHeader text={'Competitive Champions'} vertical={false}/>
                    <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={targets.competitive} date__gte={competitive_from} setDateGte={set_competitive_from}/>
                    <Box sx={{width:'100%', height:'100%'}}>
                        <TableDisplayGrid
                            display_all={false}
                            request={() => competitive_api.aggregate(request_parameters_1)}
                            rowStyle={rowStyle.none}
                            disp_columns={['champion']}
                            sortBy={"games"}
                            paramObject={request_parameters_1}
                            metrics={metrics}
                        />
                    </Box>
                </Stack>

                <Stack  sx={{width:'33%', height:'850px'}} direction={'column'} spacing={3}>
                    <SectionHeader text={'SoloQ Champions'} vertical={false}/>
                    <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={targets.soloq} date__gte={soloq_from} setDateGte={set_soloq_from}/>
                    <Box sx={{width:'100%', height:'100%'}}>
                        <TableDisplayGrid
                            display_all={false}
                            request={() => soloq_api.aggregate(request_parameters_2)}
                            rowStyle={rowStyle.none}
                            disp_columns={['champion']}
                            sortBy={"games"}
                            paramObject={request_parameters_2}
                            metrics={metrics}
                        />
                    </Box>
                </Stack>

                <Stack  sx={{width:'33%', height:'850px'}} direction={'column'} spacing={3}>
                    <SectionHeader text={'Comfort on Meta'} vertical={false}/>
                    <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={targets.soloq} date__gte={soloq_from} setDateGte={set_soloq_from}/>
                    <MetaComfort competitive_request_parameters={request_parameters_1} soloq_request_parameters={request_parameters_2} meta_champions={meta_champions}/>
                </Stack>
    </Stack>
}

function MetaComfort(props: {competitive_request_parameters: any, soloq_request_parameters: any, meta_champions: any[]}){
    const context = useReferentialContext();
    const competitive_api = useReferentialContext().farsightApi.getSummariesApi(targets.competitive);
    const soloq_api = useReferentialContext().farsightApi.getSummariesApi(targets.soloq);
    const [competitiveData, setCompetitiveData] = useState([] as any[]);
    const [soloqData, setSoloqData] = useState([] as any[]);
    const [competitiveDone, setCompetitiveDone] = useState(false);
    const [soloqDone, setSoloqDone] = useState(false);
    const [joinedData, setJoinedData] = useState([] as any[]);
    const [updateObject, setUpdateObject] = useState({} as any);

    useEffect(() => {
        competitive_api.aggregate({
            ...props.competitive_request_parameters,
            index_on: ['champion'],
        }).then(r => setCompetitiveData(r)).then(() => setCompetitiveDone(true));
        soloq_api.aggregate({
            ...props.soloq_request_parameters,
            index_on: ['champion'],
        }).then(r => setSoloqData(r)).then(() => setSoloqDone(true));

    }, [props.competitive_request_parameters.watermark, props.soloq_request_parameters.watermark])

    useEffect(() => {
        if(soloqDone && competitiveDone && props.meta_champions.length > 0){
            const joined_data = [];
            for(let i in props.meta_champions){
                const rating_row = props.meta_champions[i];
                const competitive_data_row = competitiveData[rating_row.champion]
                const soloq_data_row = soloqData[rating_row.champion]
                const joined_object = {
                    champion: rating_row.champion,
                    in_meta_games: rating_row.games,

                    competitive_games: competitive_data_row? competitive_data_row[0].games : undefined,
                    competitive_winrate: competitive_data_row? competitive_data_row[0].winrate : undefined,
                    competitive_performance: competitive_data_row? competitive_data_row[0].performance : null,
                    competitive_relative_performance: competitive_data_row? competitive_data_row[0].relative_performance : null,

                    soloq_games: soloq_data_row? soloq_data_row[0].games : undefined,
                    soloq_winrate: soloq_data_row? soloq_data_row[0].winrate : undefined,
                    soloq_performance: soloq_data_row? soloq_data_row[0].performance : null,
                    soloq_relative_performance: soloq_data_row? soloq_data_row[0].relative_performance : null,
                }
                joined_data.push(joined_object);
            }
            setSoloqDone(false);
            setCompetitiveDone(false);
            setJoinedData(joined_data);

            const update = {watermark: JSON.stringify(joined_data)};
            setUpdateObject(update);
        }
    }, [soloqDone, competitiveDone, props.meta_champions])

    async function request(){
        return joinedData;
    }

    const columnGroupingModel= [
        {
            groupId: "Competitive",
            children: [{ field: "competitive_games" }, { field: "competitive_winrate" }, { field: "competitive_performance" }, { field: "competitive_relative_performance" }],
            headerClassName: "header",
        },
        {
            groupId: "SoloQ",
            children: [{ field: "soloq_games" }, { field: "soloq_winrate" }, { field: "soloq_performance" }, { field: "soloq_relative_performance" }],
            headerClassName: "header"
        }
    ]

    return <Box sx={{width:'100%', height:'100%'}}>
        <TableDisplayGrid
            display_all={false}
            request={() => request()}
            rowStyle={rowStyle.none}
            disp_columns={['champion', 'in_meta_games','competitive_games','competitive_winrate','competitive_performance','competitive_relative_performance','soloq_games','soloq_winrate','soloq_performance','soloq_relative_performance']}
            sortBy={"in_meta_games"}
            paramObject={updateObject}
            metrics={[]}
            grouping_model={columnGroupingModel}
        />
    </Box>
}

const sectionHeaderStyle = (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: theme.typography.h5.fontSize,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    "& svg": {
        marginRight: theme.spacing(1),
    },
    justifyContent: "center",
});

function SectionHeader(props: { text: string, vertical: boolean }) {
    return (
        <Box component={"fieldset"} sx={{ ...sectionHeaderStyle, ...props.vertical ? {textOrientation: 'sideways', writingMode: 'vertical-lr'} : {}}} >
            <Typography variant="h5" align={"center"} aria-orientation={'vertical'}>
                {props.text}
            </Typography>
        </Box>
    );
}

